import Image from 'next/image'
import * as React from 'react'

import type { NonFsdsLpTestimonial } from '@/components/learning-path/non-fsds-learning-path-content'

interface NonFsdsLpTestimonialsSectionProps {
  title: string
  subtitle: string
  testimonials: NonFsdsLpTestimonial[]
}

export default function NonFsdsLpTestimonialsSection({
  title,
  subtitle,
  testimonials,
}: NonFsdsLpTestimonialsSectionProps) {
  // Split into 2 columns for staggered masonry effect
  const midpoint = Math.ceil(testimonials.length / 2)
  const leftColumn = testimonials.slice(0, midpoint)
  const rightColumn = testimonials.slice(midpoint)

  return (
    <section data-testid="non-fsds-lp-testimonials-section" className="bg-white px-4 py-14 sm:px-6 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl md:text-4xl">{title}</h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600 sm:text-lg">{subtitle}</p>
        </div>

        {/* Staggered masonry grid */}
        <div className="mt-10 columns-1 gap-5 space-y-5 sm:columns-2">
          {/* Left column cards */}
          {leftColumn.map((item, index) => (
            <TestimonialCard key={`left-${index}`} testimonial={item} />
          ))}
          {/* Right column cards */}
          {rightColumn.map((item, index) => (
            <TestimonialCard key={`right-${index}`} testimonial={item} />
          ))}
        </div>
      </div>
    </section>
  )
}

function TestimonialCard({ testimonial }: { testimonial: NonFsdsLpTestimonial }) {
  return (
    <article className="break-inside-avoid rounded-2xl border border-blue-100/80 bg-blue-50/50 p-5 ring-1 ring-transparent transition-all duration-200 hover:ring-blue-200 hover:shadow-sm sm:p-6">
      {/* Quote icon */}
      <svg className="mb-3 size-7 text-blue-300" viewBox="0 0 24 24" fill="currentColor">
        <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
      </svg>

      <blockquote className="text-base leading-relaxed text-slate-700">{testimonial.quote}</blockquote>

      {/* Author */}
      <div className="mt-4 flex items-center gap-3">
        <Image
          src={testimonial.avatar}
          alt={testimonial.name}
          width={40}
          height={40}
          className="size-10 rounded-full object-cover"
        />
        <div>
          <p className="text-sm font-semibold text-slate-900">{testimonial.name}</p>
          <p className="text-xs text-slate-500">{testimonial.role}</p>
        </div>
      </div>
    </article>
  )
}
